import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { createAxiosInstance } from "api/axiosConfig.js";
import { PATIENT_FAMILY_MEMBER_BY_ID } from "api/Service.js";
import { Dialog } from "components/ui/Dialog";
import { SimplePatientPageLayout } from "features/patients/components/SimplePatientPageLayout/SimplePatientPageLayout";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { formatPatientAge } from "features/patients/utils";
import { pathGenerators } from "utils/routes";

import { PatientFamilySearch } from "./components/PatientFamilySearch/PatientFamilySearch";
import { PatientsFamilyTable as PatientFamilyTable } from "./components/PatientFamilyTable/PatientFamilyTable";

const formatDob = (dob) => {
  if (!dob) return "";
  const parts = dob.split("-");
  if (parts.length !== 3) return dob;

  return `${parts[2]}/${parts[1]}/${parts[0]} (${formatPatientAge(dob)})`;
};

const PatientFamilyPage = () => {
  const navigate = useNavigate();
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const patientId = useCurrentPatientId();

  const [relationships, setRelationships] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState({ open: false, patientRelationship: null });

  const fetchPatientFamily = async () => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}${PATIENT_FAMILY_MEMBER_BY_ID}${patientId}`
      );
      setRelationships(
        response.data
          .filter(Boolean)
          .map((element) => ({
            id: element.id,
            patientId: element.relatedToPatient.id,
            patientname:
              element.relatedToPatient.firstName +
              (element.relatedToPatient.surname
                ? " " + element.relatedToPatient.surname
                : ""),
            dob: formatDob(element.relatedToPatient.dateOfBirth),
            familyRelationType: element.familyRelationType,
            canSeeRecords: element.canSeeRecords,
          }))
      );
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchPatientFamily();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on mount only
  }, []);

  const goToPatientFamily = (id) => {
    navigate(pathGenerators.patient.family(id));
  };

  const handleOpenModal = (patientRelationship) => {
    setIsModalOpen({ open: true, patientRelationship: patientRelationship ?? null });
  };
  const handleCloseModal = () => {
    setIsModalOpen({ open: false, patientRelationship: null });
  };

  return (
    <>
      <SimplePatientPageLayout>
        <PatientFamilyTable
          relationshipListList={relationships}
          onAdd={() => handleOpenModal()}
          onEdit={handleOpenModal}
          onPatientClick={goToPatientFamily}
          refetchRelationships={fetchPatientFamily}
        />
      </SimplePatientPageLayout>

      <Dialog
        open={isModalOpen.open}
        title="Add family member"
        onClose={handleCloseModal}
        width={560}
      >
        <PatientFamilySearch
          currentPatientId={patientId}
          closeModal={handleCloseModal}
          refetchRelationships={fetchPatientFamily}
          initialPatientRelationship={isModalOpen.patientRelationship}
        />
      </Dialog>
    </>
  );
};

export { PatientFamilyPage };
